<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import liveReports from "dashboard/api/liveReports";
import {
  fetchGroupedConversations,
  fetchDailyMovements,
  fetchMessagesByAgent,
  fetchScopedSummary,
} from "dashboard/mostChatwootExtensions/reportRequests";

const store = useStore();
const selectedInboxIds = ref([]);
const selectedPeriod = ref("today");
const loading = ref(false);
const dashboardNow = ref(new Date());
const localDateString = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
const startDate = ref(localDateString(new Date()));
const endDate = ref(localDateString(new Date()));
const appliedDateRange = ref(null);
const dateRangeError = computed(() => {
  if (!startDate.value || !endDate.value)
    return "Selecione a data inicial e final.";
  const start = Date.parse(startDate.value + "T00:00:00Z");
  const end = Date.parse(endDate.value + "T00:00:00Z");
  if (!Number.isFinite(start) || !Number.isFinite(end))
    return "Datas inválidas.";
  if (end < start) return "A data final deve ser igual ou posterior à inicial.";
  if ((end - start) / 86400000 + 1 > 31)
    return "Selecione no máximo 31 dias, incluindo início e fim.";
  return "";
});
const applyDateRange = () => {
  if (dateRangeError.value) return;
  appliedDateRange.value = {
    since: Math.floor(new Date(startDate.value + "T00:00:00").getTime() / 1000),
    until: Math.floor(new Date(endDate.value + "T23:59:59").getTime() / 1000),
  };
  selectedPeriod.value = "custom";
};

const summary = ref({
  conversations_count: 0,
  resolutions_count: 0,
  human_avg_first_response_time: null,
  human_first_response_count: 0,
  most_avg_resolution_time: null,
  most_resolution_count: 0,
});

const movements = ref({
  opened_count: null,
  closed_count: null,
  reopened_count: null,
  coverage_complete: false,
  tracking_from: null,
});
const movementLoaded = ref(false);
const dashboardError = ref("");
const movementSuffix = computed(() =>
  new Date(periodRange.value.since * 1000).toDateString() ===
  new Date(periodRange.value.until * 1000).toDateString()
    ? "no dia"
    : "no período",
);
const formatMovementCount = (value) =>
  value === null || value === undefined ? "—" : formatNumber(value);

const metrics = ref({
  open: 0,
  pending: 0,
  unassigned: 0,
  unattended: 0,
});

const agentMetrics = ref([]);
const messageAgentMetrics = ref([]);
const conversationSeries = ref([]);
const resolutionSeries = ref([]);

const inboxes = computed(() => store.getters["inboxes/getInboxes"] || []);
const agents = computed(() => store.getters["agents/getAgents"] || []);

const formatNumber = (value) =>
  new Intl.NumberFormat("pt-BR").format(Number(value || 0));

const formatDuration = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "—";
  }

  const seconds = Math.round(Number(value));

  if (seconds < 60) return `${seconds}s`;

  if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return remainingSeconds
      ? `${minutes}m ${remainingSeconds}s`
      : `${minutes}m`;
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
};

const periodRange = computed(() => {
  if (selectedPeriod.value === "custom" && appliedDateRange.value)
    return appliedDateRange.value;
  const now = dashboardNow.value;
  const start = new Date(now);

  if (selectedPeriod.value === "today") start.setHours(0, 0, 0, 0);

  if (selectedPeriod.value === "7days") {
    start.setDate(start.getDate() - 6);
    start.setHours(0, 0, 0, 0);
  }

  if (selectedPeriod.value === "30days") {
    start.setDate(start.getDate() - 29);
    start.setHours(0, 0, 0, 0);
  }

  return {
    since: Math.floor(start.getTime() / 1000),
    until: Math.floor(now.getTime() / 1000),
  };
});

const periodLabel = computed(() => {
  const labels = {
    today: "Hoje",
    "7days": "Últimos 7 dias",
    "30days": "Últimos 30 dias",
    custom: "Período personalizado",
  };

  return labels[selectedPeriod.value];
});

const dateRangeLabel = computed(() => {
  const { since, until } = periodRange.value;
  const format = (timestamp) =>
    new Date(timestamp * 1000).toLocaleDateString("pt-BR");

  return `${format(since)} – ${format(until)}`;
});

const chartGroupBy = computed(() =>
  selectedPeriod.value === "today" ? "hour" : "day",
);

const seriesValue = (item) => Number(item.value ?? item.count ?? 0);

const maxChartCount = computed(() =>
  Math.max(
    ...conversationSeries.value.map(seriesValue),
    ...resolutionSeries.value.map(seriesValue),
    1,
  ),
);

const formatChartLabel = (timestamp) => {
  const date = new Date(Number(timestamp) * 1000);

  return selectedPeriod.value === "today"
    ? date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
    : date.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
};

const chartPoints = computed(() => {
  const created = new Map(
    conversationSeries.value.map((item) => [
      Number(item.timestamp),
      seriesValue(item),
    ]),
  );
  const resolved = new Map(
    resolutionSeries.value.map((item) => [
      Number(item.timestamp),
      seriesValue(item),
    ]),
  );
  const { since, until } = periodRange.value;
  const step = selectedPeriod.value === "today" ? 3600 : 86400;
  const startDate = new Date(since * 1000);
  if (step === 86400) startDate.setHours(0, 0, 0, 0);
  else startDate.setMinutes(0, 0, 0);
  const start = Math.floor(startDate.getTime() / 1000);
  const end = until;
  const points = [];

  for (let timestamp = start; timestamp <= end;) {
    points.push({
      timestamp,
      label: formatChartLabel(timestamp),
      created: created.get(timestamp) || 0,
      resolved: resolved.get(timestamp) || 0,
    });
    if (step === 86400) {
      const next = new Date(timestamp * 1000);
      next.setDate(next.getDate() + 1);
      timestamp = Math.floor(next.getTime() / 1000);
    } else {
      timestamp += step;
    }
  }

  return points;
});

const svgPoints = (values) => {
  if (!values.length) return "";
  const width = 720;
  const height = 220;
  const padding = 12;
  const usableWidth = width - padding * 2;
  const usableHeight = height - padding * 2;

  return values
    .map((value, index) => {
      const x =
        values.length === 1
          ? width / 2
          : padding + (index / (values.length - 1)) * usableWidth;
      const y = height - padding - (value / maxChartCount.value) * usableHeight;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
};

const createdPoints = computed(() =>
  movements.value.coverage_complete
    ? svgPoints(chartPoints.value.map((point) => point.created))
    : "",
);

const resolvedPoints = computed(() =>
  svgPoints(chartPoints.value.map((point) => point.resolved)),
);

const sparklinePoints = (values) => {
  const max = Math.max(...values, 1);
  const width = 180;
  const height = 42;

  return values
    .map((value, index) => {
      const x = values.length === 1 ? 0 : (index / (values.length - 1)) * width;
      const y = height - 3 - (value / max) * 34;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
};

const conversationSparkline = computed(() =>
  movements.value.coverage_complete
    ? sparklinePoints(chartPoints.value.map((point) => point.created))
    : "",
);

const resolutionSparkline = computed(() =>
  sparklinePoints(chartPoints.value.map((point) => point.resolved)),
);

const agentRows = computed(() =>
  agentMetrics.value
    .map((metric) => {
      const agent = agents.value.find(
        (item) => Number(item.id) === Number(metric.assignee_id),
      );
      const conversations = Number(metric.conversations || 0);
      const resolved = Number(metric.resolved || 0);
      const resolutionRate =
        conversations > 0 ? Math.round((resolved / conversations) * 100) : 0;

      return {
        id: metric.assignee_id,
        name: agent?.name || `Agente #${metric.assignee_id}`,
        avatarUrl: agent?.thumbnail || agent?.avatar_url || "",
        conversations,
        resolved,
        open: Number(metric.open || 0),
        resolutionRate,
        resolutionTime: formatDuration(metric.resolution_time),
      };
    })
    .sort(
      (a, b) =>
        b.conversations - a.conversations || a.name.localeCompare(b.name),
    ),
);

const statusItems = computed(() => [
  {
    label: "Conversas em andamento",
    value: Number(metrics.value.open || 0),
    color: "#2563eb",
    icon: "i-lucide-message-square",
  },
  {
    label: "Aguardando resposta",
    value: Number(metrics.value.pending || 0),
    color: "#f59e0b",
    icon: "i-lucide-clock-3",
  },
  {
    label: "Não atribuídas",
    value: Number(metrics.value.unassigned || 0),
    color: "#ef4444",
    icon: "i-lucide-user-x",
  },
  {
    label: "Não atendidas",
    value: Number(metrics.value.unattended || 0),
    color: "#8b5cf6",
    icon: "i-lucide-bell-off",
  },
]);

const statusTotal = computed(() =>
  statusItems.value.reduce((total, item) => total + item.value, 0),
);

const donutStyle = computed(() => {
  const total = statusTotal.value;

  if (!total) return { background: "#e2e8f0" };

  let cursor = 0;
  const segments = statusItems.value.map((item) => {
    const start = cursor;
    cursor += (item.value / total) * 100;
    return `${item.color} ${start}% ${cursor}%`;
  });

  return { background: `conic-gradient(${segments.join(", ")})` };
});

const messageAgentColors = [
  "#2563eb",
  "#16a34a",
  "#f59e0b",
  "#8b5cf6",
  "#ef4444",
  "#0891b2",
  "#db2777",
  "#65a30d",
  "#ea580c",
  "#4f46e5",
  "#0f766e",
  "#a855f7",
];

const messageAgentItems = computed(() =>
  messageAgentMetrics.value.map((item, index) => ({
    ...item,
    value: Number(item.value || 0),
    color: messageAgentColors[index % messageAgentColors.length],
  })),
);

const messageAgentTotal = computed(() =>
  messageAgentItems.value.reduce((total, item) => total + item.value, 0),
);

const messageAgentDonutStyle = computed(() => {
  const total = messageAgentTotal.value;

  if (!total) return { background: "#e2e8f0" };

  let cursor = 0;
  const segments = messageAgentItems.value.map((item) => {
    const start = cursor;
    cursor += (item.value / total) * 100;
    return `${item.color} ${start}% ${cursor}%`;
  });

  return { background: `conic-gradient(${segments.join(", ")})` };
});

const selectedInboxName = computed(() => {
  if (!selectedInboxIds.value.length) return "Todas as minhas caixas";

  if (selectedInboxIds.value.length === 1) {
    return (
      inboxes.value.find(
        (inbox) => Number(inbox.id) === Number(selectedInboxIds.value[0]),
      )?.name || "Caixa selecionada"
    );
  }

  return `${selectedInboxIds.value.length} caixas selecionadas`;
});

const isInboxSelected = (inboxId) =>
  selectedInboxIds.value.includes(Number(inboxId));

const toggleInbox = (inboxId) => {
  const normalizedId = Number(inboxId);

  selectedInboxIds.value = isInboxSelected(normalizedId)
    ? selectedInboxIds.value.filter((id) => id !== normalizedId)
    : [...selectedInboxIds.value, normalizedId];
};

const selectAllInboxes = () => {
  selectedInboxIds.value = inboxes.value.map((inbox) => Number(inbox.id));
};

const clearInboxSelection = () => {
  selectedInboxIds.value = [];
};

const mergeAgentMetrics = (responses) => {
  const totals = new Map();

  responses.forEach((response) => {
    (response.data || []).forEach((metric) => {
      const id = Number(metric.assignee_id);
      const current = totals.get(id) || {
        assignee_id: id,
        conversations: 0,
        open: 0,
        unattended: 0,
        unassigned: 0,
        resolved: 0,
        resolutionTimeTotal: 0,
        resolutionTimeWeight: 0,
      };
      const resolved = Number(metric.resolved || 0);
      const resolutionTime = Number(metric.resolution_time || 0);
      const weight = Math.max(resolved, 1);

      current.conversations += Number(metric.conversations || 0);
      current.open += Number(metric.open || 0);
      current.unattended += Number(metric.unattended || 0);
      current.unassigned += Number(metric.unassigned || 0);
      current.resolved += resolved;
      current.resolutionTimeTotal += resolutionTime * weight;
      current.resolutionTimeWeight += resolutionTime > 0 ? weight : 0;
      totals.set(id, current);
    });
  });

  return [...totals.values()].map((metric) => ({
    ...metric,
    resolution_time: metric.resolutionTimeWeight
      ? metric.resolutionTimeTotal / metric.resolutionTimeWeight
      : 0,
  }));
};

const mergeSummaries = (responses) => {
  const items = responses.map((response) => response.data || {});
  const conversations = items.reduce(
    (total, item) => total + Number(item.conversations_count || 0),
    0,
  );
  const resolutions = items.reduce(
    (total, item) => total + Number(item.resolutions_count || 0),
    0,
  );
  const weightedAverage = (field, weightField) => {
    let total = 0;
    let weight = 0;

    items.forEach((item) => {
      const value = Number(item[field] || 0);
      const itemWeight = Number(item[weightField] || 0);

      if (value > 0 && itemWeight > 0) {
        total += value * itemWeight;
        weight += itemWeight;
      }
    });

    return weight ? total / weight : null;
  };

  return {
    conversations_count: conversations,
    resolutions_count: resolutions,
    human_avg_first_response_time: weightedAverage(
      "human_avg_first_response_time",
      "human_first_response_count",
    ),
    human_first_response_count: items.reduce(
      (total, item) => total + Number(item.human_first_response_count || 0),
      0,
    ),
    most_avg_resolution_time: weightedAverage(
      "most_avg_resolution_time",
      "most_resolution_count",
    ),
    most_resolution_count: items.reduce(
      (total, item) => total + Number(item.most_resolution_count || 0),
      0,
    ),
  };
};

const mergeMessageAgentMetrics = (responses) => {
  const totals = new Map();

  responses.forEach((response) => {
    (response.data || []).forEach((item) => {
      const current = totals.get(item.key) || {
        key: item.key,
        label: item.label,
        value: 0,
      };
      current.value += Number(item.value || 0);
      totals.set(item.key, current);
    });
  });

  return [...totals.values()].sort(
    (a, b) => b.value - a.value || a.label.localeCompare(b.label),
  );
};

const loadInboxScope = async (inboxId) => {
  const { since, until } = periodRange.value;
  const scoped = Boolean(inboxId);
  const params = scoped ? { inbox_id: inboxId } : {};

  return Promise.all([
    liveReports.getConversationMetric(params),
    fetchGroupedConversations({
      groupBy: "assignee_id",
      inboxId: inboxId || null,
      from: since,
      to: until,
    }),
    fetchScopedSummary({
      since,
      until,
      inboxId: inboxId || undefined,
      type: scoped ? "inbox" : "account",
      id: inboxId || undefined,
      groupBy: "day",
      businessHours: false,
    }),
    fetchMessagesByAgent({
      since,
      until,
      inboxId: inboxId || undefined,
    }),
  ]);
};

let dashboardRequestId = 0;
const loadDashboard = async () => {
  const requestId = ++dashboardRequestId;
  dashboardNow.value = new Date();
  loading.value = true;
  movementLoaded.value = false;
  movements.value = { opened_count: null, closed_count: null, reopened_count: null };
  messageAgentMetrics.value = [];
  conversationSeries.value = [];
  resolutionSeries.value = [];

  try {
    const scopes = selectedInboxIds.value.length
      ? selectedInboxIds.value
      : [null];
    dashboardError.value = "";
    const { since, until } = periodRange.value;
    const [results, movementResponse] = await Promise.all([
      Promise.all(scopes.map(loadInboxScope)),
      fetchDailyMovements({
        since,
        until,
        inboxIds: selectedInboxIds.value,
        groupBy: chartGroupBy.value,
      }),
    ]);
    if (requestId !== dashboardRequestId) return;
    movements.value = movementResponse.data;
    movementLoaded.value = true;
    const metricResponses = results.map((result) => result[0]);
    const agentResponses = results.map((result) => result[1]);
    const summaryResponses = results.map((result) => result[2]);
    const messageAgentResponses = results.map((result) => result[3]);

    metrics.value = ["open", "pending", "unassigned", "unattended"].reduce(
      (totals, key) => ({
        ...totals,
        [key]: metricResponses.reduce(
          (sum, response) => sum + Number(response.data?.[key] || 0),
          0,
        ),
      }),
      {},
    );
    agentMetrics.value = mergeAgentMetrics(agentResponses);
    messageAgentMetrics.value = mergeMessageAgentMetrics(messageAgentResponses);
    summary.value = mergeSummaries(summaryResponses);
    conversationSeries.value = movements.value.opened_series || [];
    resolutionSeries.value = movements.value.closed_series || [];
  } catch (error) {
    if (requestId !== dashboardRequestId) return;
    dashboardError.value = "Não foi possível carregar os indicadores. Tente novamente.";
    movements.value = { opened_count: null, closed_count: null, reopened_count: null };
    messageAgentMetrics.value = [];
    conversationSeries.value = [];
    resolutionSeries.value = [];
    console.error("Erro ao carregar Dashboard:", error);
  } finally {
    if (requestId === dashboardRequestId) loading.value = false;
  }
};

watch([selectedInboxIds, selectedPeriod, appliedDateRange], loadDashboard, {
  deep: true,
});

onMounted(async () => {
  await Promise.all([
    store.dispatch("agents/get"),
    store.dispatch("inboxes/get"),
  ]);
  await loadDashboard();
});
</script>

<template>
  <div class="dashboard-page">
    <header class="dashboard-header">
      <div>
        <h1>Dashboard</h1>
        <p>Visão geral do atendimento em tempo real</p>
      </div>

      <div class="dashboard-filters">
        <div class="date-chip">
          <span class="i-lucide-calendar-days" />
          <span>{{ dateRangeLabel }}</span>
        </div>

        <label class="filter-field">
          <span class="i-lucide-calendar-range" />
          <select v-model="selectedPeriod" aria-label="Período">
            <option value="today">Hoje</option>
            <option value="7days">Últimos 7 dias</option>
            <option value="30days">Últimos 30 dias</option>
            <option v-if="appliedDateRange" value="custom">
              Período personalizado
            </option>
          </select>
        </label>

        <form class="custom-date-range" @submit.prevent="applyDateRange">
          <label class="filter-field"
            >Início
            <input
              v-model="startDate"
              type="date"
              aria-label="Data inicial"
              required
            />
          </label>
          <label class="filter-field"
            >Fim
            <input
              v-model="endDate"
              type="date"
              aria-label="Data final"
              required
            />
          </label>
          <button
            class="filter-field"
            type="submit"
            :disabled="Boolean(dateRangeError) || loading"
          >
            Aplicar datas
          </button>
          <span v-if="dateRangeError" class="date-range-error" role="alert">{{
            dateRangeError
          }}</span>
        </form>

        <details class="inbox-picker">
          <summary>
            <span class="i-lucide-inbox" />
            <span>{{ selectedInboxName }}</span>
            <span class="i-lucide-chevron-down" />
          </summary>

          <div class="inbox-picker__menu">
            <div class="inbox-picker__actions">
              <button type="button" @click="selectAllInboxes">
                Selecionar todas
              </button>
              <button type="button" @click="clearInboxSelection">
                Usar todas permitidas
              </button>
            </div>

            <label v-for="inbox in inboxes" :key="inbox.id">
              <input
                type="checkbox"
                :checked="isInboxSelected(inbox.id)"
                @change="toggleInbox(inbox.id)"
              />
              <span>{{ inbox.name }}</span>
            </label>

            <p v-if="!inboxes.length">
              Nenhuma caixa disponível para este usuário.
            </p>
          </div>
        </details>
      </div>
    </header>

    <div v-if="loading" class="loading-bar">
      <span />
    </div>

    <p v-if="dashboardError" role="alert" class="date-range-error">
      {{ dashboardError }}
    </p>
    <p
      v-else-if="movementLoaded && !loading && !movements.coverage_complete"
      role="status"
      class="date-range-error"
    >
      Aberturas e reaberturas indisponíveis: o período não tem histórico completo
      de mudanças de status. Encerramentos contam conversas únicas por dia.
    </p>

    <section class="metric-grid">
      <article class="metric-card">
        <div class="metric-card__top">
          <div class="metric-icon metric-icon--blue">
            <span class="i-lucide-messages-square" />
          </div>
          <div>
            <p>Abertas {{ movementSuffix }}</p>
            <strong>{{ formatMovementCount(movements.opened_count) }}</strong>
          </div>
        </div>
        <svg viewBox="0 0 180 42" aria-hidden="true">
          <polyline
            :points="conversationSparkline"
            class="sparkline sparkline--blue"
          />
        </svg>
        <small>{{ periodLabel }} · {{ selectedInboxName }}</small>
      </article>

      <article class="metric-card">
        <div class="metric-card__top">
          <div class="metric-icon metric-icon--green">
            <span class="i-lucide-circle-check-big" />
          </div>
          <div>
            <p>Encerradas</p>
            <strong>{{ formatMovementCount(movements.closed_count) }}</strong>
          </div>
        </div>
        <svg viewBox="0 0 180 42" aria-hidden="true">
          <polyline
            :points="resolutionSparkline"
            class="sparkline sparkline--green"
          />
        </svg>
        <small>Conversas únicas encerradas por dia</small>
      </article>

      <article class="metric-card">
        <div class="metric-card__top">
          <div class="metric-icon metric-icon--amber">
            <span class="i-lucide-rotate-ccw" />
          </div>
          <div>
            <p>Reabertas {{ movementSuffix }}</p>
            <strong>{{ formatMovementCount(movements.reopened_count) }}</strong>
          </div>
        </div>
        <div class="metric-context">
          <span class="i-lucide-rotate-ccw" />
          De resolvida para aberta · uma vez por dia
        </div>
      </article>

      <article class="metric-card">
        <div class="metric-card__top">
          <div class="metric-icon metric-icon--violet">
            <span class="i-lucide-gauge" />
          </div>
          <div>
            <p>Tempo médio de resolução</p>
            <strong>{{ formatDuration(summary.most_avg_resolution_time) }}</strong>
          </div>
        </div>
        <div class="metric-context">
          <span class="i-lucide-chart-no-axes-combined" />
          Fechamento confirmado após 24 horas
        </div>
      </article>

      <article class="metric-card">
        <div class="metric-card__top">
          <div class="metric-icon metric-icon--cyan">
            <span class="i-lucide-timer" />
          </div>
          <div>
            <p>Tempo de primeira resposta</p>
            <strong>{{ formatDuration(summary.human_avg_first_response_time) }}</strong>
          </div>
        </div>
        <div class="metric-context">
          <span class="i-lucide-user-check" />
          Somente respostas de agentes humanos
        </div>
      </article>
    </section>

    <section class="dashboard-main-grid">
      <article class="panel volume-panel">
        <div class="panel-header">
          <div>
            <h2>Volume de conversas</h2>
          </div>
          <div class="legend">
            <span><i class="legend-dot legend-dot--blue" />Abertas</span>
            <span><i class="legend-dot legend-dot--green" />Encerradas</span>
          </div>
        </div>

        <p v-if="!movements.coverage_complete" class="date-range-error">
          Série de aberturas indisponível para este período.
        </p>
        <div class="line-chart">
          <div class="chart-grid-lines">
            <i v-for="line in 5" :key="line" />
          </div>
          <svg
            viewBox="0 0 720 220"
            preserveAspectRatio="none"
            aria-label="Volume de conversas"
          >
            <polyline
              :points="createdPoints"
              class="chart-line chart-line--blue"
            />
            <polyline
              :points="resolvedPoints"
              class="chart-line chart-line--green"
            />
          </svg>
          <div class="chart-labels">
            <span
              v-for="(point, index) in chartPoints"
              v-show="
                index % Math.max(1, Math.ceil(chartPoints.length / 6)) === 0
              "
              :key="point.timestamp"
            >
              {{ point.label }}
            </span>
          </div>
        </div>
      </article>

      <article class="panel distribution-panel message-agent-panel">
        <div class="panel-header">
          <div>
            <h2>Mensagens por agente</h2>
            <p>Mensagens enviadas no período selecionado</p>
          </div>
        </div>

        <div class="distribution-content">
          <div class="donut" :style="messageAgentDonutStyle">
            <div>
              <strong>{{ formatNumber(messageAgentTotal) }}</strong>
              <span>Mensagens</span>
            </div>
          </div>

          <div v-if="messageAgentItems.length" class="distribution-legend">
            <div v-for="item in messageAgentItems" :key="item.key">
              <i :style="{ backgroundColor: item.color }" />
              <span>{{ item.label }}</span>
              <strong>{{ formatNumber(item.value) }}</strong>
            </div>
          </div>
          <p v-else class="message-agent-empty">
            Nenhuma mensagem enviada neste período.
          </p>
        </div>
      </article>

      <article class="panel distribution-panel">
        <div class="panel-header">
          <div>
            <h2>Distribuição atual</h2>
            <p>Conversas por situação</p>
          </div>
        </div>

        <div class="distribution-content">
          <div class="donut" :style="donutStyle">
            <div>
              <strong>{{ formatNumber(statusTotal) }}</strong>
              <span>Total</span>
            </div>
          </div>

          <div class="distribution-legend">
            <div v-for="item in statusItems" :key="item.label">
              <i :style="{ backgroundColor: item.color }" />
              <span>{{ item.label }}</span>
              <strong>{{ formatNumber(item.value) }}</strong>
            </div>
          </div>
        </div>
      </article>

      <article class="panel realtime-panel">
        <div class="panel-header">
          <div>
            <h2>Atendimentos em tempo real</h2>
            <p>Estado neste momento</p>
          </div>
          <span class="live-badge"><i />Ao vivo</span>
        </div>

        <div class="status-list">
          <div v-for="item in statusItems" :key="item.label">
            <span :class="item.icon" />
            <p>{{ item.label }}</p>
            <strong :style="{ color: item.color }">{{
              formatNumber(item.value)
            }}</strong>
          </div>
        </div>
      </article>
    </section>

    <section class="dashboard-bottom-grid">
      <article class="panel agents-panel">
        <div class="panel-header">
          <div>
            <h2>Desempenho dos agentes</h2>
            <p>Produção da equipe no período</p>
          </div>
          <span class="period-badge">{{ periodLabel }}</span>
        </div>

        <div v-if="agentRows.length" class="agent-table-wrap">
          <table class="agent-table">
            <thead>
              <tr>
                <th>Agente</th>
                <th>Conversas</th>
                <th>Resolvidas</th>
                <th>Resolução</th>
                <th>Tempo médio</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="agent in agentRows.slice(0, 8)" :key="agent.id">
                <td>
                  <span class="agent-avatar">
                    <img
                      v-if="agent.avatarUrl"
                      :src="agent.avatarUrl"
                      :alt="agent.name"
                    />
                    <b v-else>{{ agent.name.charAt(0) }}</b>
                  </span>
                  <span>{{ agent.name }}</span>
                </td>
                <td>{{ formatNumber(agent.conversations) }}</td>
                <td>{{ formatNumber(agent.resolved) }}</td>
                <td>
                  <div class="rate-cell">
                    <strong>{{ agent.resolutionRate }}%</strong>
                    <span
                      ><i
                        :style="{
                          width: `${Math.min(agent.resolutionRate, 100)}%`,
                        }"
                    /></span>
                  </div>
                </td>
                <td>{{ agent.resolutionTime }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else class="empty-state">
          <span class="i-lucide-users" />
          <p>Nenhum dado de agente para os filtros selecionados.</p>
        </div>
      </article>
    </section>
  </div>
</template>

<style scoped>
.custom-date-range {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.custom-date-range input {
  min-width: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  border: 0;
}
.custom-date-range button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.date-range-error {
  width: 100%;
  color: #dc2626;
  font-size: 12px;
}

.dashboard-page {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 100%;
  padding: 24px;
  background: radial-gradient(
      circle at 82% 0%,
      rgba(37, 99, 235, 0.05),
      transparent 28%
    ),
    var(--color-n-background, #f8fafc);
  color: var(--color-n-slate-12, #0f172a);
}

.dashboard-header,
.dashboard-filters,
.metric-card__top,
.panel-header,
.legend,
.distribution-content,
.avatar-stack {
  display: flex;
  align-items: center;
}

.dashboard-header {
  justify-content: space-between;
  gap: 20px;
}

.dashboard-header h1 {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  letter-spacing: -0.025em;
}

.dashboard-header p,
.panel-header p {
  margin: 4px 0 0;
  color: var(--color-n-slate-10, #64748b);
  font-size: 13px;
}

.dashboard-filters {
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.date-chip,
.filter-field,
.period-badge,
.live-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 38px;
  padding: 0 12px;
  border: 1px solid var(--color-n-weak, #e2e8f0);
  border-radius: 9px;
  background: var(--color-n-solid-2, #fff);
  color: var(--color-n-slate-11, #334155);
  font-size: 12px;
}

.filter-field select {
  max-width: 210px;
  border: 0;
  outline: 0;
  background: transparent;
  color: inherit;
  font: inherit;
}

.inbox-picker {
  position: relative;
}

.inbox-picker summary {
  display: flex;
  min-width: 210px;
  min-height: 38px;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  border: 1px solid var(--color-n-weak, #e2e8f0);
  border-radius: 9px;
  background: var(--color-n-solid-2, #fff);
  color: var(--color-n-slate-11, #334155);
  cursor: pointer;
  font-size: 12px;
  list-style: none;
}

.inbox-picker summary::-webkit-details-marker {
  display: none;
}

.inbox-picker summary span:nth-child(2) {
  min-width: 0;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.inbox-picker__menu {
  position: absolute;
  z-index: 20;
  top: calc(100% + 6px);
  right: 0;
  width: 280px;
  max-height: 330px;
  overflow-y: auto;
  padding: 8px;
  border: 1px solid var(--color-n-weak, #e2e8f0);
  border-radius: 10px;
  background: var(--color-n-solid-2, #fff);
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.16);
}

.inbox-picker__menu label {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 9px;
  border-radius: 7px;
  cursor: pointer;
  font-size: 12px;
}

.inbox-picker__menu label:hover {
  background: var(--color-n-alpha-2, #f1f5f9);
}

.inbox-picker__menu input {
  accent-color: #2563eb;
}

.inbox-picker__menu p {
  margin: 8px;
  color: var(--color-n-slate-10, #64748b);
  font-size: 12px;
}

.inbox-picker__actions {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding: 4px 4px 8px;
  border-bottom: 1px solid var(--color-n-weak, #e2e8f0);
  margin-bottom: 4px;
}

.inbox-picker__actions button {
  border: 0;
  background: transparent;
  color: #2563eb;
  cursor: pointer;
  font-size: 10px;
  font-weight: 600;
}

.loading-bar {
  height: 3px;
  overflow: hidden;
  border-radius: 999px;
  background: #dbeafe;
}

.loading-bar span {
  display: block;
  width: 36%;
  height: 100%;
  border-radius: inherit;
  background: #2563eb;
  animation: loading 1s ease-in-out infinite alternate;
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
}

.metric-card,
.panel {
  border: 1px solid var(--color-n-weak, #e2e8f0);
  border-radius: 12px;
  background: var(--color-n-solid-2, #fff);
  box-shadow: 0 5px 20px rgba(15, 23, 42, 0.045);
}

.metric-card {
  min-width: 0;
  padding: 16px;
}

.metric-card__top {
  gap: 12px;
}

.metric-icon {
  display: grid;
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  place-items: center;
  border-radius: 11px;
  font-size: 21px;
}

.metric-icon--blue {
  color: #2563eb;
  background: #dbeafe;
}
.metric-icon--green {
  color: #16a34a;
  background: #dcfce7;
}
.metric-icon--amber {
  color: #d97706;
  background: #fef3c7;
}
.metric-icon--violet {
  color: #9333ea;
  background: #f3e8ff;
}
.metric-icon--cyan {
  color: #0891b2;
  background: #cffafe;
}

.metric-card p {
  margin: 0 0 3px;
  color: var(--color-n-slate-11, #475569);
  font-size: 12px;
}

.metric-card strong {
  display: block;
  overflow: hidden;
  font-size: clamp(20px, 2vw, 27px);
  line-height: 1.1;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.metric-card svg {
  width: 100%;
  height: 42px;
  margin-top: 10px;
  overflow: visible;
}

.sparkline,
.chart-line {
  fill: none;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.sparkline {
  stroke-width: 2.2;
}
.sparkline--blue {
  stroke: #3b82f6;
}
.sparkline--green {
  stroke: #22c55e;
}

.metric-card small,
.metric-context {
  color: var(--color-n-slate-10, #64748b);
  font-size: 11px;
}

.metric-context {
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 52px;
  padding-top: 18px;
}

.avatar-stack {
  min-height: 52px;
  padding-top: 14px;
}

.avatar-stack img,
.avatar-stack > span,
.avatar-stack b,
.agent-avatar {
  display: grid;
  width: 30px;
  height: 30px;
  place-items: center;
  overflow: hidden;
  border: 2px solid var(--color-n-solid-2, #fff);
  border-radius: 50%;
  background: #dbeafe;
  color: #1d4ed8;
  font-size: 11px;
}

.avatar-stack img,
.avatar-stack > span,
.avatar-stack b {
  margin-left: -6px;
}

.avatar-stack img:first-child,
.avatar-stack > span:first-child {
  margin-left: 0;
}

.avatar-stack b {
  background: #eff6ff;
}

.avatar-stack small {
  margin-left: 2px;
}

.dashboard-main-grid {
  display: grid;
  grid-template-columns:
    minmax(0, 1.45fr)
    minmax(250px, 0.9fr)
    minmax(250px, 0.9fr)
    minmax(230px, 0.75fr);
  gap: 12px;
}

.dashboard-bottom-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 12px;
}

.panel {
  min-width: 0;
  padding: 16px;
}

.panel-header {
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 16px;
}

.volume-panel .panel-header {
  align-items: flex-start;
  flex-direction: column;
  gap: 8px;
}

.volume-panel .panel-header h2 {
  white-space: nowrap;
}

.panel-header h2 {
  margin: 0;
  font-size: 14px;
  font-weight: 650;
}

.legend {
  gap: 13px;
  color: var(--color-n-slate-10, #64748b);
  font-size: 11px;
}

.legend span {
  display: flex;
  align-items: center;
  gap: 6px;
}

.legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.legend-dot--blue {
  background: #3b82f6;
}
.legend-dot--green {
  background: #22c55e;
}

.line-chart {
  position: relative;
  height: 245px;
  padding-top: 4px;
}

.line-chart svg {
  position: relative;
  z-index: 2;
  width: 100%;
  height: 210px;
}

.chart-line {
  vector-effect: non-scaling-stroke;
  stroke-width: 2.4;
}

.chart-line--blue {
  stroke: #2563eb;
}
.chart-line--green {
  stroke: #22c55e;
}

.chart-grid-lines {
  position: absolute;
  inset: 4px 0 31px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.chart-grid-lines i {
  width: 100%;
  border-top: 1px solid var(--color-n-weak, #e2e8f0);
}

.chart-labels {
  display: flex;
  justify-content: space-between;
  color: var(--color-n-slate-10, #64748b);
  font-size: 10px;
}

.distribution-content {
  flex-direction: column;
  justify-content: center;
  gap: 16px;
  min-height: 220px;
}

.donut {
  display: grid;
  width: 126px;
  height: 126px;
  flex: 0 0 126px;
  place-items: center;
  border-radius: 50%;
}

.donut > div {
  display: grid;
  width: 76px;
  height: 76px;
  place-items: center;
  align-content: center;
  border-radius: 50%;
  background: var(--color-n-solid-2, #fff);
}

.donut strong {
  font-size: 23px;
  line-height: 1;
}

.donut span {
  margin-top: 5px;
  color: var(--color-n-slate-10, #64748b);
  font-size: 11px;
}

.distribution-legend {
  display: flex;
  width: 100%;
  min-width: 0;
  flex-direction: column;
  gap: 13px;
}

.distribution-legend > div {
  display: grid;
  grid-template-columns: 8px 1fr auto;
  align-items: center;
  gap: 8px;
  font-size: 11px;
}

.distribution-legend i {
  width: 8px;
  height: 8px;
  border-radius: 3px;
}

.message-agent-panel .distribution-legend {
  max-height: 164px;
  overflow-y: auto;
  padding-right: 4px;
}

.message-agent-empty {
  margin: 0;
  color: var(--color-n-slate-10, #64748b);
  font-size: 11px;
  text-align: center;
}

.live-badge {
  min-height: 25px;
  padding: 0 8px;
  border-color: #bbf7d0;
  background: #f0fdf4;
  color: #15803d;
  font-size: 10px;
}

.live-badge i {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #22c55e;
}

.status-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.status-list > div {
  display: grid;
  grid-template-columns: 20px 1fr auto;
  align-items: center;
  gap: 8px;
  min-height: 43px;
  border-bottom: 1px solid var(--color-n-weak, #e2e8f0);
  font-size: 12px;
}

.status-list > div:last-child {
  border-bottom: 0;
}

.status-list p {
  margin: 0;
}

.status-list strong {
  font-size: 18px;
}

.period-badge {
  min-height: 30px;
  padding: 0 10px;
  font-size: 10px;
}

.agent-table-wrap {
  overflow-x: auto;
}

.agent-table {
  width: 100%;
  min-width: 640px;
  border-collapse: collapse;
  font-size: 12px;
}

.agent-table th {
  padding: 0 10px 10px;
  color: var(--color-n-slate-10, #64748b);
  font-size: 10px;
  font-weight: 500;
  text-align: left;
}

.agent-table td {
  padding: 9px 10px;
  border-top: 1px solid var(--color-n-weak, #e2e8f0);
}

.agent-table td:first-child {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 550;
}

.agent-avatar {
  width: 27px;
  height: 27px;
  flex: 0 0 27px;
  border: 0;
}

.agent-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.rate-cell {
  display: grid;
  grid-template-columns: 38px minmax(55px, 1fr);
  align-items: center;
  gap: 7px;
}

.rate-cell strong {
  color: #16a34a;
  font-size: 11px;
}

.rate-cell > span {
  height: 5px;
  overflow: hidden;
  border-radius: 999px;
  background: #dcfce7;
}

.rate-cell i {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: #22c55e;
}

.empty-state {
  display: grid;
  min-height: 180px;
  place-items: center;
  align-content: center;
  gap: 8px;
  color: var(--color-n-slate-10, #64748b);
  text-align: center;
}

.empty-state > span {
  font-size: 26px;
}

.empty-state p {
  margin: 0;
  font-size: 12px;
}

@keyframes loading {
  from {
    transform: translateX(-20%);
  }
  to {
    transform: translateX(200%);
  }
}

@media (max-width: 1280px) {
  .metric-grid {
    grid-template-columns: repeat(5, minmax(0, 1fr));
  }
  .dashboard-main-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .realtime-panel {
    grid-column: auto;
  }
}

@media (max-width: 900px) {
  .dashboard-header {
    align-items: flex-start;
    flex-direction: column;
  }
  .dashboard-filters {
    justify-content: flex-start;
  }
  .metric-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .dashboard-main-grid,
  .dashboard-bottom-grid {
    grid-template-columns: 1fr;
  }
  .realtime-panel {
    grid-column: auto;
  }
}

@media (max-width: 600px) {
  .dashboard-page {
    padding: 16px;
  }
  .metric-grid {
    grid-template-columns: 1fr;
  }
  .dashboard-filters,
  .filter-field,
  .date-chip,
  .inbox-picker,
  .inbox-picker summary {
    width: 100%;
  }
  .filter-field select {
    width: 100%;
    max-width: none;
  }
  .inbox-picker__menu {
    left: 0;
    right: auto;
    width: 100%;
  }
  .distribution-content {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
